import { TreeItem } from "./tree-item";
import { TreeItemSubselect } from "./tree-item-subselect";
import { SingleSubselect, SingleSubselectListener } from "./tree-select";


/** 
 * Manages selection for a single list of items under a given parent node.
 * Selecting an item automatically deselects any previously selected item at the same level.
 * Keeps item selected and expanded state in sync (for expandable items), so that selecting an item automatically expands it to show any subitems, and deselecting an item automatically collapses it.
 * 
 * This component does not manage changes which may occur across different levels of the tree.
 * For instance, if an item in a higher level is selected, any selected items in lower levels should be deselected and collapsed, but this must be implemented externally in the selection mode.
 **/
export class SingleTreeItemSubselect
    extends TreeItemSubselect<string, SingleTreeItemSubselect, SingleSubselectListener>
    implements SingleSubselect {

    get value(): string | null { 
        return this.selectedItem?.value || null;
    }

    set value(value: string | null) {
        this.selectedItem = this.getItem(value);
    }

    /** Returns the currently selected item, or null if no item is selected */
    public get selectedItem(): TreeItem|null {
        const value = this.element.dataset.value;
        if (!value) {
            return null;
        }
        return this.items.find(option => option.value === value) || null;
    }

    private set selectedItem(item: TreeItem|null) { 
        // Only 1 item can be selected at a time, so clear any prior selection
        const selectedItem = this.selectedItem;
        if (selectedItem) {
            // If the item is already selected, avoid deselect/reselect cycle which may cause screen flicker.
            if (selectedItem.value === item?.value) {
                return;
            } 
            // Otherwise deselect the previous item and collapse any subitems if applicable
            else {
                this.setItemSelected(selectedItem, false);
            }
         }

        // Item will be null if deselecting; otherwise, select the new item and expand any subitems
        if (item) {
            this.setItemSelected(item, true);
        }

        // Finally, keep the data-value in sync with the selected item
        this.element.dataset.value = item?.value || "";
    }

    /** 
     * Sets the selected state of the given item and updates its expanded state accordingly.
     * This is a helper method for set selectedItem, not intended for use in other methods of this class.
     **/
    private setItemSelected(item: TreeItem, selected: boolean): void {
        item.highlighted = selected;
        // Bypass typical TreeItem selection behavior which would trigger callbacks and cause duplicated action; instead, directly set the selected state and expanded state of the item
        if (item.toggle) {
            item.toggle.selected = selected;
        }
        if (item.list) {
            item.list.expanded = selected; 
        }
    }

    transitionToSelectedState(item: TreeItem, selected: boolean): void {
        this.selectedItem = selected ? item: null;
        this.callbacks?.onValueChange(this);
    }

    transitionToExpandedState(item: TreeItem, expanded: boolean): void {
        // In single-selection mode, expansion is coupled to selection state, and SingleTreeItemSubselect already does the necessary
        // expansion behavior by setting `selectedItem` and item.list.expanded. By keeping this no-op we avoid duplicated action
        // when shared toggle elements fire both select and expand events.
    }

    onDescendantSelected(): void {
        // If delegated, move to single selection mode implementation
        // Keep logical selection state for branch tracking, but remove highlight from ancestors.
        const selectedItem = this.selectedItem;
        if (selectedItem) {
            selectedItem.highlighted = false;
        }
    }

    onAncestorSelected(): void {
        this.element.remove();
    }
}